// =============================================================================
// PickerRequestView.jsx — the DEDICATED picker view of a request + its SOs.
// =============================================================================
// Rendered by RequestDetail when isPicker (a Picker, or a Supervisor/Superuser/
// picker-mode Combined who opened FROM the Pickers List). It owns its OWN layout —
// no picker conditionals bolted onto the responder view — composing only what a
// picker needs to process the order:
//   • General Information (read-only) + People-on-request
//   • the shelter's caseworker note (why a different delivery address, etc.)
//   • one panel per approved picker item ON THIS SALES ORDER (option (b): scoped to
//     the SO the picker opened from the Pickers List, via scopeSoNumber / ?picker_so)
//     with the EST / ACTUAL cost and the supplier ORDER No. editable, SO No. read-only
//   • a "Completed" button — enabled only once every item has an actual price + an
//     order number — which saves the edits (db-117 set_line_picker_fields, the
//     edit-wipe-safe RPC), releases the held SOs (db-113 hold → queued), flips this
//     picker's task(s) for the request to Actioned, and returns to the Pickers List.
//
// The shared read-only atoms (ChildItemReadView) are used elsewhere; the picker's
// EDITING lives here so the responder view stays clean.
// =============================================================================
import React from "react";
import { useShowDeviceDelivery } from "../../app/UiConfigContext.jsx";
import {
  completePickerTaskBySoNumber,
  completePickerTasksForRequest,
  findPickerLockTask,
} from "../../data/requestTasks";
import {
  getPushByOrderNumber,
  releaseHeldPushByOrderNumber,
  releaseHeldPushesForRequest,
} from "../../data/salesOrderPushes";
import { getSalesOrderByNumber, writeLinePickerFields } from "../../data/salesOrders";
import { confirmDialog } from "../../services/dialogService";
import {
  appendOrderNumbersToSoLines,
  postOrderConfirmationOnce,
  updateSoLinePrices,
  updateSoLineProductCodes,
} from "../../services/pickerCompletion";
import { costWarnings } from "../../services/pickerCostCheck";
import { logAudit } from "../../utils/auditLog";
import { APPROVAL_STATUSES } from "../../utils/enums";
import { formatPhone } from "../../utils/sanitize";
import { CaseworkerNotesStrip } from "./CaseworkerNotesStrip.jsx";
import { ProductPickerModal } from "./ProductPickerModal.jsx";
import {
  COMPLETE_GATE_MSG,
  costEqualsEstimateMsg,
  NOT_YOUR_TASK_MSG,
  TASK_NOT_CLOSED_MSG,
} from "./pickerCompleteMessages";
import { RequestPeopleModal } from "./RequestPeopleModal.jsx";

const APPROVED = APPROVAL_STATUSES.APPROVED;

// The request's picker items: approved Devices + approved QLD Stationery fees, each
// carrying the child context and the child-row id needed to save the picker's edits.
function collectPickerItems(req) {
  const out = [];
  (req.children || []).forEach((c, ci) => {
    const meta = [c.gender, c.yearLevel, c.schoolName].filter(Boolean).join(" · ");
    if (c.laptop && c.laptop.approvalStatus === APPROVED) {
      out.push({
        key: `dev-${ci}`,
        kind: "device",
        id: c.laptop.id,
        childLabel: `Child ${ci + 1}`,
        childMeta: meta,
        title: c.laptop.deviceType || "Device",
        noteLabel: "Why Needed",
        note: c.laptop.whyNeeded || "",
        delivery: c.laptop.deliveryAddress || req.deliveryAddress || "",
        soNumber: c.laptop.deviceSoNumber || "",
        amount: c.laptop.estimatedCost,
        orderNumber: c.laptop.deviceOrderNumber || "",
      });
    }
    (c.fees || []).forEach((f, fi) => {
      if (f.feeType === "Stationery" && f.approvalStatus === APPROVED) {
        out.push({
          key: `sta-${ci}-${fi}`,
          kind: "fee",
          id: f.id,
          childLabel: `Child ${ci + 1}`,
          childMeta: meta,
          title: "Stationery Pack",
          noteLabel: "Description",
          note: f.feeDesc || "",
          delivery: req.deliveryAddress || "",
          soNumber: f.feesSoNumber || "",
          amount: f.feeAmount,
          orderNumber: f.feesOrderNumber || "",
        });
      }
    });
  });
  return out;
}

const BLUE = "#1E88E5";
const BORDER = "#CFE0DE";
const fieldLabel = {
  fontSize: 11,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  marginBottom: 4,
};
const roValue = {
  fontSize: 14,
  color: "#1A2E2B",
  background: "#F7F9F9",
  border: `1px solid ${BORDER}`,
  borderRadius: 7,
  padding: "9px 12px",
  minHeight: 20,
};
const editInput = {
  width: "100%",
  fontSize: 14,
  color: "#1A2E2B",
  background: "white",
  border: "2px solid #00897B",
  borderRadius: 7,
  padding: "8px 12px",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

function ROField({ label, value }) {
  return (
    <div>
      <div style={fieldLabel}>{label}</div>
      <div style={roValue}>{value || "—"}</div>
    </div>
  );
}

export function PickerRequestView({ req, user, onDone, scopeSoNumber, assignedToOther }) {
  // Admin-controlled per-device delivery visibility (default off). System Setup → Config.
  const showDeviceDelivery = useShowDeviceDelivery();
  // Option (b): scope to the ONE SO the picker opened from the Pickers List — show only
  // the items whose stamped SO number matches. No scope (rare fallback) → show all.
  const items = React.useMemo(() => {
    const all = collectPickerItems(req);
    return scopeSoNumber ? all.filter((it) => it.soNumber === scopeSoNumber) : all;
  }, [req, scopeSoNumber]);
  const [edits, setEdits] = React.useState(function () {
    const m = {};
    for (const it of items) {
      m[it.key] = {
        amount: it.amount != null && it.amount !== "" ? String(it.amount) : "",
        orderNumber: it.orderNumber || "",
      };
    }
    return m;
  });
  const [showPeople, setShowPeople] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [msgErr, setMsgErr] = React.useState(false);
  // { key, current } of the device item whose Product Code picker modal is open.
  const [pickerFor, setPickerFor] = React.useState(null);
  // The scoped SO's line product codes (Unleashed stock codes: STAP/STA1/… by year
  // for stationery, the DEV placeholder for devices), in line order, so each item
  // shows what will be ordered. Read-only — stationery is year-coded automatically
  // and devices are substituted in Unleashed by the picker.
  const [productCodes, setProductCodes] = React.useState([]);
  // LOCK: once this SO's push has left 'hold' (Completed released it), the task is
  // done — lock the whole view for EVERY user so a re-open can't re-price the SO or
  // re-queue the push. 'hold' = still editable; any other status = locked. No scope
  // (rare fallback) or no push row → not locked (unchanged behaviour).
  const [locked, setLocked] = React.useState(false);
  React.useEffect(
    function () {
      let cancelled = false;
      if (!scopeSoNumber) {
        setProductCodes([]);
        setLocked(false);
        return;
      }
      getSalesOrderByNumber(scopeSoNumber).then(function (so) {
        if (cancelled) return;
        const codes = (so?.lines || [])
          .filter(function (l) {
            return l.product_code && l.product_code !== "DISCOUNT";
          })
          .map(function (l) {
            return l.product_code;
          });
        setProductCodes(codes);
      });
      getPushByOrderNumber(scopeSoNumber).then(function (p) {
        if (cancelled) return;
        setLocked(!!p && p.status !== "hold");
      });
      return function () {
        cancelled = true;
      };
    },
    [scopeSoNumber]
  );

  function setField(key, field, value) {
    setEdits(function (prev) {
      return { ...prev, [key]: { ...prev[key], [field]: value } };
    });
  }

  // Completed is enabled only once EVERY item has an ACTUAL price that DIFFERS from
  // the seeded estimate — higher or lower — AND an order number (John, 28 Jul). The
  // cost field pre-fills with the estimate, so a plain "> 0" check passed on load
  // without the picker confirming anything; requiring a change forces a real actual
  // cost. If the true price genuinely equals the estimate the picker enters a
  // standard price to move it off the estimate.
  const allReady =
    items.length > 0 &&
    items.every(function (it) {
      const e = edits[it.key] || {};
      const amt = Number(e.amount);
      const est = Number(it.amount);
      return amt > 0 && amt !== est && String(e.orderNumber).trim() !== "";
    });

  // Helper text while Completed is disabled (26 Oct 2026). If any line's cost equals
  // its estimate the picker is stuck with no way forward, so that message wins over
  // the generic one — even if an order number is still missing (David's call).
  const equalsEstimate = items.find(function (it) {
    const amt = Number(edits[it.key]?.amount);
    return amt > 0 && amt === Number(it.amount);
  });
  const gateMsg = equalsEstimate
    ? costEqualsEstimateMsg(
        `${equalsEstimate.childLabel} — ${equalsEstimate.title}`,
        equalsEstimate.amount
      )
    : COMPLETE_GATE_MSG;

  async function onCompleted() {
    // Cost sanity check (25 Oct 2026 — 2026-03269 / 03270 typos reached prod and
    // Unleashed). Asked BEFORE anything is checked or saved; "Go back" saves nothing.
    // The estimate is the seeded line amount (it.amount). Rules: pickerCostCheck.ts.
    const warnings = costWarnings(
      items.map(function (it) {
        return {
          label: `${it.childLabel} — ${it.title}`,
          kind: it.kind,
          amount: edits[it.key]?.amount,
          estimate: it.amount,
        };
      })
    );
    if (warnings.length > 0) {
      const ok = await confirmDialog({
        title: "Check this cost",
        message: `${warnings.join("\n")}\n\nCheck the decimal point and the number of digits. Is this cost right?`,
        confirmLabel: "Yes, the cost is right",
        cancelLabel: "Go back and fix",
        danger: true,
      });
      if (!ok) return;
      logAudit("picker_cost_override_confirmed", { so_number: scopeSoNumber, warnings }, req.id);
    }
    setSaving(true);
    setMsg("");
    setMsgErr(false);
    // Re-check, at the moment of the click, that this SO's task is still assigned to
    // this account — the on-open gate can go stale if the task is released or
    // reassigned while the page is open. Checked BEFORE any save or release.
    if (scopeSoNumber) {
      const held = await findPickerLockTask(req.id, scopeSoNumber);
      if (!held || !user?.id || held.assigned_to_id !== user.id) {
        setSaving(false);
        setMsgErr(true);
        setMsg(NOT_YOUR_TASK_MSG);
        return;
      }
    }
    const devices = [];
    const fees = [];
    items.forEach(function (it, idx) {
      const e = edits[it.key] || {};
      const row = {
        id: String(it.id),
        amount: Number(e.amount),
        orderNumber: String(e.orderNumber).trim(),
      };
      if (it.kind === "device") {
        // Persist the EFFECTIVE code (the picker's choice, else the code currently shown
        // from the SO line) so the child row + responder view always reflect what's on
        // screen — even when the picker leaves the pre-filled code unchanged.
        const effective =
          e.productCode ||
          productCodes[idx] ||
          (productCodes.length === 1 ? productCodes[0] : null);
        row.productCode = effective || null;
        devices.push(row);
      } else {
        fees.push(row);
      }
    });
    const saved = await writeLinePickerFields(devices, fees);
    if (saved.error) {
      setSaving(false);
      setMsg(`Save failed: ${saved.error.message}`);
      return;
    }
    // Finalise the SO lines BEFORE releasing the held push, so the payload the push
    // worker builds from request_sales_orders carries the picker's ACTUAL price +
    // order number (not the seeded estimate). Both are best-effort — never block the
    // completion. The item order matches the SO's non-DISCOUNT lines (child order).
    await updateSoLinePrices(
      scopeSoNumber,
      items.map(function (it) {
        return edits[it.key]?.amount;
      })
    );
    await appendOrderNumbersToSoLines(
      scopeSoNumber,
      items.map(function (it) {
        return edits[it.key]?.orderNumber;
      })
    );
    await updateSoLineProductCodes(
      scopeSoNumber,
      items.map(function (it) {
        return edits[it.key]?.productCode;
      })
    );
    // Release the held push — just THIS SO when scoped (option (b)), else all the
    // request's held SOs (the no-scope fallback).
    const released = scopeSoNumber
      ? await releaseHeldPushByOrderNumber(scopeSoNumber)
      : await releaseHeldPushesForRequest(req.id);
    if (released.error) {
      setSaving(false);
      setMsg(`Edits saved, but release failed: ${released.error.message}`);
      return;
    }
    // Flip the picker task to Actioned so it leaves the Pickers List — just THIS SO's
    // task when scoped, else all of this user's picker tasks for the request. Assignee-
    // scoped so the db-60 guard passes and a co-picker's other-SO task is untouched.
    // Safety net: 0 rows updated means the task was NOT closed (not ours by the time
    // of the update) — say so instead of reporting success, and skip the audit entry.
    let taskClosed = false;
    if (user?.id) {
      const now = new Date().toISOString();
      const done = scopeSoNumber
        ? await completePickerTaskBySoNumber(req.id, scopeSoNumber, user.id, now)
        : await completePickerTasksForRequest(req.id, user.id, now);
      taskClosed = !done.error && (done.data?.length ?? 0) > 0;
      if (taskClosed) {
        logAudit(
          "picker_task_actioned",
          {
            request_id: req.id,
            ref: req.refNo,
            so_number: scopeSoNumber || null,
            by: user.shelterName,
          },
          req.id
        );
      }
    }
    // Send the DFV Service a once-per-day "Order Confirmation" comment (the SO lines
    // were finalised above, before the push was released). Best-effort.
    // A picker SO is homogeneous (device OR stationery) — the item type fills the
    // {{item_type}} token and keys the once-per-day-per-type dedupe.
    const orderType = items.some(function (it) {
      return it.kind === "device";
    })
      ? "Device"
      : "Stationery";
    await postOrderConfirmationOnce(req, user?.shelterName, orderType);
    setSaving(false);
    if (!taskClosed) {
      // Stay on the page so the picker sees the warning.
      setMsgErr(true);
      setMsg(TASK_NOT_CLOSED_MSG);
      return;
    }
    setMsg("Completed — the sales order has been released to Unleashed ✓");
    // Return to the Pickers List (brief pause so the picker sees the confirmation).
    if (onDone) setTimeout(onDone, 900);
  }

  return (
    <div>
      {/* Supervisor viewing an SO held by another picker: view-only (Completed is
          disabled). A peer picker never reaches here — RequestDetail blocks them. */}
      {assignedToOther && (
        <div
          style={{
            background: "#FFF8E1",
            border: "1px solid #FFB300",
            borderRadius: 8,
            padding: "8px 14px",
            marginBottom: 14,
            fontSize: 13,
            fontWeight: 600,
            color: "#795548",
          }}
        >
          Assigned to {assignedToOther} — view only. Release it from the Pickers List to take it on.
        </div>
      )}
      {/* General Information + People-on-request (no DFV notes / summary panels). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 12,
          marginBottom: 14,
          alignItems: "start",
        }}
      >
        <div
          style={{
            border: `1px solid ${BORDER}`,
            borderRadius: 10,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: BLUE,
              color: "white",
              padding: "0 18px",
              height: 48,
              display: "flex",
              alignItems: "center",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            General Information
          </div>
          <div style={{ padding: "14px 16px" }}>
            {[
              ["DFV Service Ref", req.shelterRef],
              ["DFV Service", req.shelterName],
              ["Contact Name", req.contactName],
              ["Contact Email", req.shelterEmail],
              [
                "Mobile / Phone",
                [req.mobile ? formatPhone(req.mobile) : "", req.phone ? formatPhone(req.phone) : ""]
                  .filter(Boolean)
                  .join("  ·  "),
              ],
            ].map(function (pair) {
              return pair[1] ? (
                <div
                  key={pair[0]}
                  style={{
                    display: "flex",
                    marginBottom: 6,
                    paddingBottom: 6,
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  <div style={{ ...fieldLabel, width: 120, flexShrink: 0, marginBottom: 0 }}>
                    {pair[0]}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1A2E2B" }}>{pair[1]}</div>
                </div>
              ) : null;
            })}
            {/* Delivery: label + address on one row (matches the fields above) to save vertical space. */}
            <div style={{ display: "flex", alignItems: "flex-start", marginTop: 4 }}>
              <div style={{ ...fieldLabel, width: 120, flexShrink: 0, marginBottom: 0 }}>
                Delivery
              </div>
              <div style={{ fontSize: 13, color: "#1A2E2B", lineHeight: 1.5, flex: 1 }}>
                {req.deliveryAddress || "—"}
              </div>
            </div>
          </div>
        </div>
        <div />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={function () {
              setShowPeople(true);
            }}
            title="See everyone who has worked on this request"
            style={{
              background: "#E3F2FD",
              color: "#37474F",
              border: `1px solid ${BORDER}`,
              borderRadius: 6,
              padding: "6px 14px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            People on this request
          </button>
        </div>
      </div>

      <CaseworkerNotesStrip req={req} />

      {items.length === 0 ? (
        <div style={{ padding: 20, color: "#607D79", fontSize: 14 }}>
          No approved Device or Stationery Pack items to process on this request.
        </div>
      ) : (
        items.map(function (it, itIdx) {
          const e = edits[it.key] || {};
          const ordered = String(e.orderNumber).trim() !== "";
          // Per-child SOs line up one code per item; an aggregated SO (e.g. devices
          // sharing one DEV line) has a single code that applies to every item.
          const productCode =
            productCodes[itIdx] || (productCodes.length === 1 ? productCodes[0] : "");
          return (
            <div
              key={it.key}
              style={{
                border: `1px solid ${BORDER}`,
                borderRadius: 10,
                marginBottom: 14,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#E8F4EA",
                  padding: "10px 16px",
                  borderBottom: `1px solid ${BORDER}`,
                  fontSize: 14,
                  color: "#1A2E2B",
                }}
              >
                <strong>{it.childLabel}</strong>
                {it.childMeta ? ` — ${it.childMeta}` : ""}
              </div>
              <div style={{ padding: 16 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1.3fr 1fr 1fr 1fr",
                    gap: 12,
                  }}
                >
                  <ROField label={it.kind === "device" ? "Device Type" : "Item"} value={it.title} />
                  {locked ? (
                    <ROField
                      label={
                        ordered
                          ? "Actual Cost - Ordered by Zephyr"
                          : "Est. Cost - GST Excluded Price"
                      }
                      value={e.amount || "—"}
                    />
                  ) : (
                    <div>
                      <div style={fieldLabel}>
                        {ordered
                          ? "Actual Cost - Ordered by Zephyr"
                          : "Est. Cost - GST Excluded Price"}
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={e.amount}
                        placeholder="0.00"
                        onChange={function (ev) {
                          setField(it.key, "amount", ev.target.value);
                        }}
                        style={editInput}
                      />
                    </div>
                  )}
                  <ROField label="SO No." value={it.soNumber} />
                  {it.kind === "device" && !locked ? (
                    <div>
                      <div style={fieldLabel}>Product Code</div>
                      <button
                        type="button"
                        onClick={function () {
                          setPickerFor({ key: it.key, current: e.productCode ?? productCode });
                        }}
                        style={{
                          ...editInput,
                          cursor: "pointer",
                          textAlign: "left",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>{(e.productCode ?? productCode) || "Choose…"}</span>
                        <span style={{ color: "#00695C", fontSize: 12 }}>{"▾"}</span>
                      </button>
                    </div>
                  ) : (
                    <ROField label="Product Code" value={e.productCode ?? productCode} />
                  )}
                  {locked ? (
                    <ROField label="Order No." value={e.orderNumber || "—"} />
                  ) : (
                    <div>
                      <div style={fieldLabel}>Order No.</div>
                      <input
                        type="text"
                        value={e.orderNumber}
                        placeholder="Enter once ordered"
                        onChange={function (ev) {
                          setField(it.key, "orderNumber", ev.target.value);
                        }}
                        style={editInput}
                      />
                    </div>
                  )}
                </div>
                {it.note && (
                  <div style={{ marginTop: 12 }}>
                    <div style={fieldLabel}>{it.noteLabel}</div>
                    <div style={roValue}>{it.note}</div>
                  </div>
                )}
                {showDeviceDelivery && (
                  <div style={{ marginTop: 12 }}>
                    <div style={fieldLabel}>Delivery Address</div>
                    <div style={roValue}>{it.delivery || "—"}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {items.length > 0 && locked && (
        <div
          style={{
            marginTop: 8,
            padding: "8px 14px",
            background: "#E8F4EA",
            border: "1px solid #A5D6A7",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            color: "#2E7D32",
          }}
        >
          This order has been completed and is locked — reopening it is view-only.
        </div>
      )}
      {items.length > 0 && !locked && (
        <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 8 }}>
          {!allReady && <span style={{ fontSize: 12, color: "#607D79" }}>{gateMsg}</span>}
          {msg && (
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: msgErr || msg.toLowerCase().includes("fail") ? "#C62828" : "#2E7D32",
              }}
            >
              {msg}
            </span>
          )}
          {/* Right-aligned — where responders expect the primary action button. */}
          <button
            type="button"
            disabled={!allReady || saving || !!assignedToOther}
            onClick={onCompleted}
            style={{
              marginLeft: "auto",
              background: "#2E7D32",
              color: "white",
              border: "none",
              borderRadius: 8,
              padding: "10px 24px",
              fontSize: 15,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: allReady && !saving && !assignedToOther ? "pointer" : "not-allowed",
              opacity: allReady && !saving && !assignedToOther ? 1 : 0.5,
            }}
          >
            {saving ? "Working..." : "Completed"}
          </button>
        </div>
      )}

      {showPeople && (
        <RequestPeopleModal
          requestId={req.id}
          refNo={req.refNo}
          onClose={function () {
            setShowPeople(false);
          }}
        />
      )}

      {pickerFor && (
        <ProductPickerModal
          currentCode={pickerFor.current}
          onSelect={function (code) {
            setField(pickerFor.key, "productCode", code);
            setPickerFor(null);
          }}
          onClose={function () {
            setPickerFor(null);
          }}
        />
      )}
    </div>
  );
}
